import {
	Button,
	Icon,
	Accordion,
	InputInLabel,
	SelectCountry,
	Checkbox,
	InputRange,
	Loader,
} from '@/components/ui';
import {
	getUserCurrentCity,
	getServicesWords,
	getCountriesData,
	getLanguagesWords,
	getNationalistWords,
	setCurrentCity,
	setUserCurrentDistrict,
	setCity,
	setCountrySlug,
	setCountry,
	getUserCurrentDistrict,
} from '@/stores/slices/userSlice';
import React, {
	useEffect,
	useRef,
	useState,
	useMemo,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hide, showFilter } from '@/stores/slices/showFilterSlice';
import {
	getFilterState,
	getFilterEditObj,
	setFilterEditObj,
	getAllProfilesCount,
} from '@/stores/slices/filterSlice';
import { plural } from '@/helper/plural';
import useLadyService from '@/services/LadyService';

import { Popup } from '../popups';
import './filter.scss';
import FilterData, { StatusOrderArray } from './FilterData';
import { useFilterLogicContext } from './FilterLogicProvider';
import { buildFilterParams } from './FilterParameterBuilder';
import { UrlAllowedWords } from '@/components/filter/FilterData';
import showToast from '../toast/Toast';
import {
	resetPage,
	setAllModels,
	setStories,
} from '@/stores/slices/modelsState';
import useHandleLocationClick from '@/services/HandleLocationClick';
import GeoInput from '../ui/input/GeoInput';
import { getScrolledClass } from '@/stores/slices/headerSlice';
import { setProfileCount } from '@/stores/slices/mapModelSlice';
import { clearTitle } from '@/stores/slices/titleSlice';
import useCityChange from '@/hooks/useCityChange';
import { makeRequest } from '@/services/makeRequest';
import { useToggleCountryCZ } from '../ui/select/SelectCountry';
import { useNavigate, useParams } from 'react-router-dom';

const Filter = (props) => {
	const { lang, t } = useLadyService();

	const dispatch = useDispatch();

	const FILTER = useFilterLogicContext();

	const filterData = FilterData();

	const changeCity = useCityChange();

	const currentCity = useSelector(getUserCurrentCity);
	const currentDistrict = useSelector(getUserCurrentDistrict);
	const isFilterOpen = useSelector(showFilter);
	const servicesCategory = useSelector(getServicesWords);
	const filterValue = useSelector(getFilterState);
	const countriesData = useSelector(getCountriesData);
	const isFilterEdit = useSelector(getFilterEditObj);
	const count = useSelector(getAllProfilesCount);

	const langData = useSelector(getLanguagesWords);
	const nationData = useSelector(getNationalistWords);

	const priceRef = useRef();
	const ageRef = useRef();

	const toggle = useToggleCountryCZ();

	// ─── NEW: pending city change (deferred until Apply) ──────────
	const [pendingCityChange, setPendingCityChange] = useState(null);

	// ─── NEW: disabled filters fetched from API per city ──────────
	const [disabledFilters, setDisabledFilters] = useState([]);

	// Helper: check if a filter value is disabled for the selected city
	const isFilterDisabled = useCallback(
		(id) => new Set(disabledFilters.disabledFilters).has(id),
		[disabledFilters],
	);
	// ──────────────────────────────────────────────────────────────

	const mapLocalFilter = useCallback((filterObj, reverse = false) => {
		const result = { ...filterObj };

		const processValue = (value) => {
			if (
				Array.isArray(value) &&
				value.every((item) => typeof item === 'string')
			) {
				return reverse ? Array.from(value) : new Set(value);
			} else if (value instanceof Set && reverse) {
				return Array.from(value);
			} else if (
				value &&
				typeof value === 'object' &&
				!Array.isArray(value)
			) {
				const processedObj = { ...value };
				Object.keys(processedObj).forEach((nestedKey) => {
					processedObj[nestedKey] = processValue(
						processedObj[nestedKey],
					);
				});
				return processedObj;
			}
			return value;
		};

		Object.keys(result).forEach((key) => {
			result[key] = processValue(result[key]);
		});

		return result;
	}, []);

	const {
		sex: categorySex,
		additional: categoryAdditional,
		bdsm: categoryBdsm,
		extreme: categoryExtreme,
		massage: categoryMassage,
		striptease: categoryStriptease,
	} = servicesCategory;

	const [editFilterName, setEditFilterName] = useState(
		isFilterEdit ? isFilterEdit.title : '',
	);
	const [editFilterError, setEditFilterError] = useState(false);
	const [currentActive, setCurrentActive] = useState('');
	const [localFilter, setLocalFilter] = useState(
		isFilterEdit
			? mapLocalFilter(isFilterEdit?.filter)
			: mapLocalFilter(FILTER.filterState),
	);

	const [defaultFilterState, setDefaultFilterState] = useState(localFilter);

	const navigate = useNavigate();

	const popupRef = useRef(null);
	const childrenRef = useRef(null);

	const plural_end = useMemo(() => {
		return plural(lang, count);
	}, [lang, count]);

	const handleClose = () => {
		if (props.setOpen) {
			props.setOpen(false);
		}
		if (isFilterEdit) {
			dispatch(setFilterEditObj(null));
		}
		dispatch(hide());

		// Reset the pending city change so the UI reverts to the Redux state
		setPendingCityChange(null);
		// Reset disabled filters back to empty (will re-fetch on next open)
		setDisabledFilters([]);
	};

	const handleClickOutside = (event) => {
		if (
			props.not_popup &&
			popupRef.current &&
			!popupRef.current.contains(event.target)
		) {
			if (props.setOpen) {
				props.setOpen(false);
			}
			event.stopPropagation();
		}

		if (
			popupRef.current &&
			popupRef.current.contains(event.target) &&
			childrenRef.current &&
			!childrenRef.current.contains(event.target)
		) {
			if (isFilterEdit) {
				dispatch(setFilterEditObj(null));
				FILTER.resetFilter(false);
			}
			dispatch(hide());
			if (props.setOpen) {
				props.setOpen(false);
			}
			event.stopPropagation();
		}
	};

	// Вспомогательная функция для получения вложенного значения
	const getNestedValue = (obj, keyPath) => {
		return keyPath.split(':').reduce((current, part) => {
			return current?.[part];
		}, obj);
	};

	const sortByOrder = (arr, order, key = 'value') => {
		const orderMap = order.reduce((acc, curr, index) => {
			acc[curr] = index;
			return acc;
		}, {});
		return [...arr].sort((a, b) => {
			const aVal = typeof a === 'object' && a ? a[key] : a;
			const bVal = typeof b === 'object' && b ? b[key] : b;

			const aIndex = orderMap[aVal] ?? Infinity;
			const bIndex = orderMap[bVal] ?? Infinity;

			return aIndex - bIndex;
		});
	};

	const EditFilterWrapper = ({ children }) => {
		if (isFilterEdit) {
			return <div className="d-flex gap-16 w-100">{children}</div>;
		} else {
			return children;
		}
	};

	// ─── NEW: handler for SelectCountry deferred selections ──────
	const handleCitySelect = (selection) => {
		console.log(`=> Selection: `, selection);
		setPendingCityChange(selection);
	};
	// ──────────────────────────────────────────────────────────────

	// ─── NEW: useEffect to fetch disabled filters per city ───────
	// Whenever the user picks a city (or country) in SelectCountry,
	// fetch which filter options are unavailable for that city.
	const fetchDisabledFilters = async (city_id, dist_slug) => {
		try {
			if (!city_id) return;

			const route = `filters/${city_id}`;
			const payload = {
				...(dist_slug && { district: dist_slug }),
			};
			const method = 'GET';

			const data = await makeRequest({ route, method, payload });

			if (data.status) {
				const disabled = FILTER.parseDisabledFilters(
					data?.data?.filters ?? [],
				);
				console.log(`=> disabled: `, disabled);
				setDisabledFilters(disabled ?? []);
			} else {
				throw new Error('DATA FALSE', data);
			}
		} catch (e) {
			console.error('Failed to fetch disabled filters:', e);
			setDisabledFilters([]);
		}
	};

	useEffect(() => {
		const slug =
			pendingCityChange?.type === 'city'
				? pendingCityChange?.data?.slug
				: pendingCityChange?.type === 'country'
					? pendingCityChange?.slug
					: null;

		if (!slug || !isFilterOpen || isFilterEdit) return;

		console.log('PENDING: ', pendingCityChange);

		fetchDisabledFilters(
			pendingCityChange?.data?.id,
			pendingCityChange.dist?.slug,
		);
	}, [pendingCityChange, isFilterOpen]);
	// ──────────────────────────────────────────────────────────────

	const handleApplyFilter = () => {
		if (localFilter.gender.size === 0) {
			localStorage.removeItem('save_gender_filter');
		}
		const reversed = mapLocalFilter(localFilter, true);
		dispatch(resetPage());

		if (pendingCityChange) {
			if (pendingCityChange.type === 'city') {
				console.log(`=> Pending city: `, pendingCityChange);
				const i = pendingCityChange.data;
				const dist = pendingCityChange.dist;

				///////
				// Update Redux with the selected city
				dispatch(
					setCurrentCity({
						id: i.id,
						title: i.name,
						icon: pendingCityChange.countryCode || currentCity.icon,
						highlight: i.highlight,
						slug: i.slug,
						districts: i.districts,
					}),
				);

				if (i.districts?.some((item) => item?.id === dist?.id)) {
					dispatch(setUserCurrentDistrict(dist));
				} else {
					dispatch(setUserCurrentDistrict({}));
				}

				dispatch(setCity(i.slug));
				dispatch(setProfileCount(i.profiles_count));
				dispatch(clearTitle());
				dispatch(setAllModels([]));
				dispatch(setStories([]));

				changeCity(
					i.slug,
					i.districts?.some((item) => item?.id === dist?.id)
						? dist
						: null,
				);

				const params = `/${lang}/${i.slug}${
					i.districts.some((item) => item?.id === dist?.id)
						? `_${dist?.slug}`
						: ``
				}`;

				navigate(params);
			} else if (pendingCityChange.type === 'country') {
				console.log(`=> Pending country: `, pendingCityChange);
				toggle();
			}
		}

		FILTER.handleApplyFilter(
			{
				...reversed,
				sity_slug: pendingCityChange?.slug || currentCity.slug,
				dist_slug:
					pendingCityChange?.dist?.slug || currentDistrict.slug,
			},
			handleClose,
			true,
		);
		setLocalFilter(mapLocalFilter(FILTER.filterState));
		setPendingCityChange(null);
	};

	const handleCheckBox = (e, key) => {
		const target = e.target;
		switch (target.name) {
			case 'place': {
				target.name = 'service_type';
				break;
			}
			case 'sex': {
				target.name = 'orientation';
				break;
			}
			default:
				break;
		}

		try {
			setLocalFilter((prev) => {
				const updated = { ...prev };

				if (key) {
					// Инициализируем структуру если нужно
					const pathParts = key.split(':');
					let currentLevel = updated;
					for (let i = 0; i < pathParts.length - 1; i++) {
						const part = pathParts[i];
						if (!currentLevel[part]) {
							currentLevel[part] = {};
						}
						currentLevel = currentLevel[part];
					}

					const finalKey = pathParts[pathParts.length - 1];
					if (!currentLevel[finalKey]) {
						currentLevel[finalKey] = new Set();
					}

					const currentSet = currentLevel[finalKey];
					if (currentSet.has(target.value)) {
						currentSet.delete(target.value);
					} else {
						currentSet.add(target.value);
					}
				} else {
					// Обычный плоский ключ
					if (!updated[target.name]) {
						updated[target.name] = new Set();
					}

					const currentSet = updated[target.name];
					if (currentSet.has(target.value)) {
						currentSet.delete(target.value);
					} else {
						currentSet.add(target.value);
					}
				}

				return updated;
			});
		} catch (e) {
			console.error(e);
			console.error(
				'Error in ',
				localFilter[target.name],
				'in',
				target.value,
			);
		}
	};

	const handleRadio = (e) => {
		const target = e.target;

		try {
			setLocalFilter((prev) => {
				const updated = { ...prev };

				if (!updated[target.name]) {
					updated[target.name] = new Set();
				}

				const currentSet = updated[target.name];
				// console.log(`=> currentSet: `, currentSet);

				if (currentSet.has(target.value)) {
					// console.log(`=> should delete: `);

					updated[target.name].delete(target.value);
				} else {
					// console.log(`=> should replace: `);
					updated[target.name] = new Set();
					updated[target.name].add(target.value);
				}

				// console.log(`=> updated: `, updated[target.name]);

				return updated;
			});
		} catch (err) {
			console.error(err);
		}
	};

	const [appearanceRangeKey, setResetKey] = useState(0);

	const handleResetAcc = (keys) => {
		const keysArray = Array.isArray(keys) ? keys : [keys];

		setLocalFilter((prev) => {
			const updated = { ...prev };

			keysArray.forEach((key) => {
				if (prev[key] !== undefined) {
					if (prev[key] instanceof Set || Array.isArray(prev[key])) {
						updated[key] = new Set();
					}
				}
			});

			return updated;
		});
	};

	const checkIfEdited = (keys) => {
		const array_keys = Array.isArray(keys) ? keys : [keys];

		return array_keys.some((key) => {
			const value = getNestedValue(localFilter, key);
			if (value && value instanceof Set) {
				return value.size > 0;
			}
			return false;
		});
	};

	const weigth_ref = useRef();
	const height_ref = useRef();

	const [resetAppearanceState, setResetAppearanceState] = useState(false);

	const handleRangeChange = (key, value) => {
		const { min, max } = value;
		const updated = {
			...localFilter,
			ranges: {
				...localFilter.ranges,
				current_state: {
					...localFilter.ranges.current_state,
					[key]: value,
				},
			},
		};
		if (key === 'weight' || key === 'height') {
			const defaults = localFilter.ranges.default_state[key];
			const current = localFilter.ranges.current_state[key];
			if (min !== defaults.min || max !== defaults.max) {
				setResetAppearanceState(true);
			} else {
				setResetAppearanceState(false);
			}
		}
		setLocalFilter(updated);
	};

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingAmmount, setIsLoadingAmmount] = useState(false);

	const [isAIActive, setIsAIActive] = useState(false);
	const [aiTextfield, setAiTextfield] = useState('');

	const handleRecordAi = () => {
		alert('in development');
	};

	const handleApplyAi = () => {
		setIsAIActive(false);
	};

	const handleSaveFilter = async () => {
		setIsLoading(true);

		const reversed = mapLocalFilter(localFilter, true);

		const { result } = buildFilterParams(
			reversed,
			FILTER.filterState.ranges?.default_state || {},
			'', // Don't use existing params, build fromscratch
			UrlAllowedWords,
		);

		dispatch(
			setFilterEditObj({
				id: isFilterEdit.id,
				title: editFilterName,
				filter: { ...reversed, filter_query: result },
			}),
		);

		await FILTER.handleEditFilter(isFilterEdit.id, editFilterName, {
			...reversed,
			filter_query: result,
		});

		dispatch(hide());
		dispatch(setFilterEditObj(null));
		// FILTER.handleApplyFilter();
		setIsLoading(false);
	};

	const clearArrayFields = (obj) => {
		const out = {};
		for (const [key, val] of Object.entries(obj)) {
			out[key] =
				Array.isArray(val) && key !== 'disabled_filters' ? [] : val;
		}
		return out;
	};

	const resetAllFilters = () => {
		FILTER.resetFilter(true);
		const emptyFilter = mapLocalFilter(
			clearArrayFields(FILTER.filterState),
		);
		setPendingCityChange(null);
		setDisabledFilters([]);
		console.log('=> new filter', emptyFilter);
		setLocalFilter(emptyFilter);
		setCurrentAddress([
			{
				address: '',
				city: currentCity?.name || '',
				city_id: currentCity?.id,
			},
		]);
	};

	useEffect(() => {
		// console.log('=> FILTER', FILTER.filterState);
		setLocalFilter(mapLocalFilter(FILTER.filterState));
		setDefaultFilterState(mapLocalFilter(FILTER.filterState));
	}, [isFilterOpen]);

	useEffect(() => {
		setDefaultFilterState(mapLocalFilter(FILTER.filterState));
	}, [FILTER.filterStateIndicator]);

	const fetchCount = async () => {
		try {
			setIsLoadingAmmount(true);
			const reversed = mapLocalFilter(localFilter, true);

			const { result } = buildFilterParams(
				reversed,
				defaultFilterState.ranges?.default_state || {},
				'', // Don't use existing params, build fromscratch
				UrlAllowedWords,
			);

			await FILTER.fetchProfilesCount(
				result,
				pendingCityChange?.data?.slug ?? false,
			);
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoadingAmmount(false);
		}
	};

	const [currentAddress, setCurrentAddress] = useState([
		{
			address: '',
			city: currentCity?.name || '',
			city_id: currentCity?.id,
		},
	]);

	const setAddress = async (newAddress) => {
		if (newAddress === null) {
			setCurrentAddress((prev) => {
				const updated = [...prev];
				updated[0] = {
					...updated[0],
					address: null,
					latitude: null,
					longitude: null,
				};
				return updated;
			});
			setLocalFilter((prev) => ({
				...prev,
				radius: { ...prev.radius, latitude: 0, longitude: 0 },
			}));
		} else if (typeof newAddress === 'string') {
			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode({ address: newAddress }, (results, status) => {
				if (status === 'OK' && results[0]) {
					const resolved = results[0];
					const lat = resolved.geometry.location.lat();
					const lng = resolved.geometry.location.lng();
					setCurrentAddress((prev) => {
						// ✅ functional update
						const updated = [...prev];
						updated[0] = {
							...updated[0],
							address: resolved.formatted_address,
							latitude: lat,
							longitude: lng,
						};
						return updated;
					});
					setLocalFilter((prev) => ({
						...prev,
						radius: {
							...prev.radius,
							latitude: lat,
							longitude: lng,
						},
					}));
				}
			});
		} else {
			const lat = newAddress.geometry.location.lat();
			const lng = newAddress.geometry.location.lng();
			setCurrentAddress((prev) => {
				const updated = [...prev];
				updated[0] = {
					...updated[0],
					address: newAddress.formatted_address,
					latitude: lat,
					longitude: lng,
				};
				return updated;
			});
			setLocalFilter((prev) => ({
				...prev,
				radius: { ...prev.radius, latitude: lat, longitude: lng },
			}));
		}
	};

	const handleChange = (value) => {
		if (!value || !value.length) {
			setAddress(null);
		} else {
			setAddress(value);
		}
	};

	const { handleLocationClick } = useHandleLocationClick();

	const onLocationClick = () => {
		handleLocationClick()
			.then((result) => {
				setLocalFilter((prev) => {
					return {
						...prev,
						radius: {
							...prev.radius,
							latitude: result.latitude,
							longitude: result.longitude,
						},
					};
				});
				const displayAddress = `${result.city} ${result.road}, ${result.house_number}`;
				setCurrentAddress((prev) => {
					const updated = [...prev];
					updated[0] = {
						...updated[0],
						address: displayAddress,
						latitude: result.latitude,
						longitude: result.longitude,
					};
					return updated;
				});
			})
			.catch(() => {
				showToast({
					message: t('oops'),
					subMessage:
						'Unable to get location, check permissions and try again.',
					variant: 'error',
				});
			});

		//
	};

	const handleRadiusCheckBox = (i) => {
		setLocalFilter((prev) => {
			let newValue = new Set(prev.radius.value);

			if (newValue.has(i.value)) {
				newValue.delete(i.value);
			} else {
				newValue = new Set([i.value]);
			}
			return {
				...prev,
				radius: {
					...prev.radius,
					value: newValue,
				},
			};
		});
	};

	useEffect(() => {
		if (isFilterOpen && !isFilterEdit) {
			fetchCount();
		}
	}, [localFilter, pendingCityChange]);

	useEffect(() => {
		const handlekeyUp = (event) => {
			if (event.isComposing || event.key === 'Escape') {
				dispatch(hide());
				if (props.setOpen) {
					props.setOpen(false);
				}
				if (isFilterEdit) {
					dispatch(setFilterEditObj(null));
				}
			}
		};
		document.addEventListener('keyup', handlekeyUp);
		return () => {
			document.removeEventListener('keyup', handlekeyUp);
		};
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isFilterEdit, props.not_popup]);

	useEffect(() => {
		if (document) {
			if (isFilterOpen) {
				document.body.classList.add('modal-open');
			} else {
				document.body.classList.remove('modal-open');
			}
		}
		return () => {
			if (document) {
				document.body.classList.remove('modal-open');
			}
		};
	}, [isFilterOpen]);

	useEffect(() => {
		if (isFilterEdit?.filter) {
			setLocalFilter(mapLocalFilter(isFilterEdit.filter));
		}
	}, [isFilterEdit]);

	useEffect(() => {
		const age = localFilter.ranges.current_state.age;
		const price = localFilter.ranges.current_state.price;
		const weight = localFilter.ranges.current_state.weight;
		const height = localFilter.ranges.current_state.height;

		if (
			priceRef.current &&
			typeof priceRef.current.setValues === 'function' &&
			ageRef.current &&
			typeof ageRef.current.setValues === 'function'
		) {
			priceRef.current.setValues(price.min, price.max);
			ageRef.current.setValues(age.min, age.max);
		}

		if (
			weigth_ref.current &&
			typeof weigth_ref.current.setValues === 'function' &&
			height_ref.current &&
			typeof height_ref.current.setValues === 'function'
		) {
			weigth_ref.current.setValues(weight.min, weight.max);
			height_ref.current.setValues(height.min, height.max);
		}
	}, [localFilter]);

	const scrolledClass = useSelector(getScrolledClass);

	return (
		<Popup
			ref={popupRef}
			open={isFilterOpen}
			setOpen={(e) => {
				handleClose();
			}}
			id={'filter'}
			wrapperClazz={'filter'}
			clazz={`filter__body ${scrolledClass}`}
			onSubmit={(e) => {
				e.preventDefault();
			}}
		>
			{console.log(`=> isFiletrEdit: `, isFilterEdit)}
			<>
				{!!countriesData?.length && (
					<div className={`filter__top`}>
						<SelectCountry
							data={countriesData}
							name={'city-create-filter'}
							clazz={`button button-xs select-city`}
							arrowSize={'m'}
							type={'flag'}
							defaultTitle={
								pendingCityChange?.type === 'city'
									? pendingCityChange.data?.name ||
										pendingCityChange.data?.title +
											(pendingCityChange.dist.slug
												? ` ${pendingCityChange.dist.slug}`
												: '')
									: pendingCityChange?.type === 'country'
										? pendingCityChange.data?.[0]?.title
										: currentCity.title
							}
							currentActiveIndex={currentActive}
							isMain={false}
							activeCountry={
								pendingCityChange?.type === 'country'
									? pendingCityChange.code?.toUpperCase()
									: pendingCityChange?.type === 'city'
										? pendingCityChange.countryCode?.toUpperCase()
										: currentCity.icon
							}
							setActive={setCurrentActive}
							selectDistrict={true}
							deferApply={true}
							onSelect={handleCitySelect}
						/>

						{/* AI BLOCK */}
						{/* <button
							onClick={() => setIsAIActive(true)}
							className="button--ai button button--tetriary button-square "
						>
							{`AI ${t('filter')}`}
						</button> */}
					</div>
				)}

				<div className="filter__checkbox-buttons p-h-8  mt-20 ">
					{filterData.place.map((i) => {
						return (
							<Checkbox
								key={i.id + '_local'}
								name={i.name}
								id={i.id + '_local'}
								value={i.value}
								title={i.title}
								checked={localFilter.service_type.has(i.value)}
								buttonCheckbox={true}
								count={i.count}
								disabled={
									!isFilterEdit
										? i.disabled || isFilterDisabled(i.id)
										: false
								}
								onChange={(e) => {
									handleCheckBox(e);
								}}
							/>
						);
					})}
				</div>

				<div className="p-h-8  mt-20 filter--status">
					{sortByOrder(
						filterData.status,
						StatusOrderArray,
						'value',
					).map((i) => {
						return (
							<Checkbox
								key={i.id}
								name={i.name}
								id={i.id + '_local'}
								value={i.value}
								title={i.title}
								checked={localFilter.status.has(i.value)}
								count={i.count}
								disabled={
									!isFilterEdit
										? i.disabled || isFilterDisabled(i.id)
										: false
								}
								onChange={(e) => {
									handleCheckBox(e);
								}}
							/>
						);
					})}
				</div>

				<div className="d-flex justify-sb mt-20 p-h-8  filter--price">
					<InputRange
						ref={priceRef}
						min={
							isFilterEdit
								? 0
								: filterValue.ranges.default_state.price.min
						}
						max={
							isFilterEdit
								? 9999
								: filterValue.ranges.default_state.price.max
						}
						step={10}
						clazz={'range--price'}
						inputIdLeft={'minPriceFilter'}
						inputIdRight={'maxPriceFilter'}
						title={`${t('price')},`}
						name={'price'}
						subtitle={`${lang === 'cz' ? ' Kč' : ' €'}/${t(
							'hour',
						)}`}
						// price={lang === 'cz' ? 'czk' : ''}
						warning={true}
						onChange={(e) => handleRangeChange('price', e)}
					/>

					<InputRange
						ref={ageRef}
						min={
							isFilterEdit
								? 18
								: filterValue.ranges.default_state.age.min
						}
						max={
							isFilterEdit
								? 99
								: filterValue.ranges.default_state.age.max
						}
						step={1}
						clazz={'range--age'}
						inputIdLeft={'minAgeFilter'}
						inputIdRight={'maxAgeFilter'}
						title={`${t('age')},`}
						name={'age'}
						subtitle={` ${t('years')}`}
						warning={true}
						onChange={(e) => handleRangeChange('age', e)}
					/>
				</div>

				<Accordion
					name={'gender'}
					title={t('gender')}
					edited={
						localFilter.gender.size > 0 ||
						localFilter.orientation.size > 0
					}
					resetFunc={() => {
						handleResetAcc(['gender', 'orientation']);
					}}
				>
					<div className="filter__box p-h-8 filter--gender">
						<div className="filter__checkbox-buttons">
							{filterData.gender.map((i) => {
								return (
									<Checkbox
										key={i.id + '_local'}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										border={false}
										checked={localFilter.gender.has(
											i.value,
										)}
										count={i.count}
										buttonCheckbox={true}
										onChange={(e) => {
											handleRadio(e);
										}}
									/>
								);
							})}
						</div>
						<hr className={'w-100'} />
						<div className="filter__checkbox-buttons">
							{filterData.sex.map((i) => {
								return (
									<Checkbox
										key={i.id + '_local'}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										border={false}
										count={i.count}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										checked={localFilter.orientation.has(
											i.value,
										)}
										buttonCheckbox={true}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>
					</div>
				</Accordion>

				<Accordion
					name={'breast'}
					title={t('appearance')}
					edited={
						checkIfEdited([
							'appearance',
							'penis',
							'lips',
							'hair',
							'breast',
							'hair_len',
							'intim_hs',
							'eyes',
							'smoking',
							'tattoo',
							'piercing',
						]) || resetAppearanceState
					}
					resetFunc={() => {
						weigth_ref.current.reset();
						height_ref.current.reset();
						handleResetAcc([
							'appearance',
							'penis',
							'lips',
							'hair',
							'breast',
							'hair_len',
							'intim_hs',
							'eyes',
							'smoking',
							'tattoo',
							'piercing',
						]);
					}}
				>
					<div className="filter__box filter--price d-flex justify-sb flex-wrap-no p-h-8">
						<InputRange
							ref={weigth_ref}
							min={
								isFilterEdit
									? 40
									: filterValue.ranges.default_state.weight
											.min
							}
							max={
								isFilterEdit
									? 199
									: filterValue.ranges.default_state.weight
											.max
							}
							step={1}
							clazz={'range--weight'}
							inputIdLeft={'minWeight_local'}
							inputIdRight={'maxWeight_local'}
							resetButton={true}
							title={`${t('weight')},`}
							name={'weight'}
							subtitle={` ${t('kg')}`}
							warning={true}
							resetKey={appearanceRangeKey}
							onChange={(e) => handleRangeChange('weight', e)}
						/>

						<InputRange
							ref={height_ref}
							min={
								isFilterEdit
									? 130
									: filterValue.ranges.default_state.height
											.min
							}
							max={
								isFilterEdit
									? 299
									: filterValue.ranges.default_state.height
											.max
							}
							step={1}
							clazz={'range--height'}
							inputIdLeft={'minHeight_local'}
							inputIdRight={'maxHeight_local'}
							resetButton={true}
							title={`${t('height')},`}
							name={'height'}
							subtitle={` ${t('cm')}`}
							warning={true}
							resetKey={appearanceRangeKey}
							onChange={(e) => handleRangeChange('height', e)}
						/>
					</div>

					{(localFilter.gender.has('trans') ||
						localFilter.gender.has('male')) && (
						<div className="filter__box p-h-8 filter--breast">
							<span className={'filter__title'}>
								{t('penissize')}
							</span>
							{filterData.penis.map((i) => {
								return (
									<Checkbox
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										checked={localFilter.penis.has(i.value)}
										count={i.count}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>
					)}

					{((!localFilter.gender.has('male') &&
						localFilter.gender.size < 2) ||
						localFilter.gender.has('trans') ||
						localFilter.gender.has('female')) && (
						<div className="filter__box p-h-8 filter--breast">
							<span className={'filter__title'}>
								{t('breastsize')}
							</span>
							{filterData.breast_size.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										count={i.count}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										checked={localFilter.breast.has(
											i.value,
										)}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}

							<div className="filter__checkbox-buttons">
								{filterData.breast.map((i) => {
									return (
										<Checkbox
											key={i.id + '_local'}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={i.title}
											buttonCheckbox={true}
											count={i.count}
											disabled={
												!isFilterEdit
													? i.disabled ||
														isFilterDisabled(i.id)
													: false
											}
											checked={localFilter.appearance.has(
												i.value,
											)}
											onChange={(e) => {
												handleCheckBox(e);
											}}
										/>
									);
								})}
							</div>
						</div>
					)}

					<div className="filter__box p-h-8 filter--hair">
						<span className={'filter__title'}>{t('hair')}</span>
						{/*  */}
						<div className="filter__checkboxes-grid">
							{filterData.hair.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										count={i.count}
										checked={localFilter.hair.has(i.value)}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>

						<div className="filter__checkbox-buttons">
							{filterData.hair_size.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										buttonCheckbox={true}
										count={i.count}
										checked={localFilter.hair_len.has(
											i.value,
										)}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>

						<span className={'filter__title'}>
							{t('pubichair')}
						</span>
						<div className="filter__checkbox-buttons">
							{filterData.hair_intim.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										buttonCheckbox={true}
										count={i.count}
										checked={localFilter.intim_hs.has(
											i.value,
										)}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>
					</div>

					<div className="filter__box p-h-8 filter--eye">
						<span className={'filter__title'}>{t('eyes')}</span>

						<div className="filter__checkboxes-grid">
							{filterData.eyes.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										count={i.count}
										checked={localFilter.eyes.has(i.value)}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>

						<div className="filter__checkbox-buttons">
							{filterData.eyes_type.map((i) => {
								return (
									<Checkbox
										key={i.id + '_local'}
										name={i.name}
										id={i.id + '_local'}
										value={i.value}
										title={i.title}
										buttonCheckbox={true}
										count={i.count}
										checked={localFilter.appearance.has(
											i.value,
										)}
										disabled={
											!isFilterEdit
												? i.disabled ||
													isFilterDisabled(i.id)
												: false
										}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>
					</div>

					<div className="filter__box p-h-8 filter--special">
						<span className={'filter__title'}>{t('features')}</span>

						<div className="filter__row">
							<span className={'filter__title'}>{t('lips')}</span>
							<div className="filter__checkbox-buttons">
								{filterData.lips.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id + '_local'}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={i.title}
											buttonCheckbox={true}
											count={i.count}
											checked={localFilter.lips.has(
												i.value,
											)}
											disabled={
												!isFilterEdit
													? i.disabled ||
														isFilterDisabled(i.id)
													: false
											}
											onChange={(e) => {
												handleCheckBox(e);
											}}
										/>
									);
								})}
							</div>
						</div>

						<div className="filter__row">
							<span className={'filter__title'}>
								{t('tattoos')}
							</span>
							<div className="filter__checkbox-buttons">
								{filterData.tattoo.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id + '_local'}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={i.title}
											buttonCheckbox={true}
											count={i.count}
											checked={localFilter.tattoo.has(
												i.value,
											)}
											disabled={
												!isFilterEdit
													? i.disabled ||
														isFilterDisabled(i.id)
													: false
											}
											onChange={(e) => {
												handleCheckBox(e);
											}}
										/>
									);
								})}
							</div>
						</div>

						<div className="filter__row">
							<span className={'filter__title'}>
								{t('piercing')}
							</span>
							<div className="filter__checkbox-buttons">
								{filterData.piercing.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id + '_local'}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={i.title}
											buttonCheckbox={true}
											count={i.count}
											checked={localFilter.piercing.has(
												i.value,
											)}
											disabled={
												!isFilterEdit
													? i.disabled ||
														isFilterDisabled(i.id)
													: false
											}
											onChange={(e) => {
												handleCheckBox(e);
											}}
										/>
									);
								})}
							</div>
						</div>

						<div className="filter__row">
							<span className={'filter__title'}>
								{t('smoking')}
							</span>
							<div className="filter__checkbox-buttons">
								{filterData.smoke.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id + '_local'}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={i.title}
											buttonCheckbox={true}
											count={i.count}
											checked={localFilter.smoking.has(
												i.value,
											)}
											disabled={
												!isFilterEdit
													? i.disabled ||
														isFilterDisabled(i.id)
													: false
											}
											onChange={(e) => {
												handleCheckBox(e);
											}}
										/>
									);
								})}
							</div>
						</div>
					</div>
				</Accordion>

				<Accordion
					name={'services'}
					title={t('services')}
					edited={checkIfEdited(['services', 'for'])}
					resetFunc={() => handleResetAcc(['services', 'for'])}
				>
					<div className="filter__box p-h-8 filter--services">
						<>
							<span className={'filter__title'}>
								{t('servicesfor')}
							</span>
							<div className="filter__checkbox-buttons">
								{filterData.services.for.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id}
											name={i.name}
											id={i.id + '_local'}
											value={i.value}
											title={t(i.title)}
											count={i.count}
											buttonCheckbox={true}
											checked={localFilter.for.has(
												i.value,
											)}
											onChange={(e) => handleCheckBox(e)}
										/>
									);
								})}
							</div>

							{!localFilter.service_type.has('massage') && (
								<>
									<span className={'filter__title'}>
										{categorySex?.title}
									</span>
									<div className="filter__checkboxes-grid">
										{filterData.services.sex.map((i) => {
											return (
												<Checkbox
													border={false}
													key={i.id}
													name={i.name}
													id={i.id}
													value={i.value}
													title={t(i.title)}
													count={i.count}
													checked={localFilter.services.has(
														i.value,
													)}
													onChange={(e) =>
														handleCheckBox(e)
													}
												/>
											);
										})}
									</div>
								</>
							)}

							{!localFilter.service_type.has('massage') && (
								<>
									<span className={'filter__title'}>
										{categoryStriptease?.title}
									</span>
									<div className="filter__checkboxes-grid">
										{filterData.services.strip.map((i) => {
											return (
												<Checkbox
													border={false}
													key={i.id}
													name={i.name}
													id={i.id}
													value={i.value}
													title={t(i.title)}
													count={i.count}
													checked={localFilter.services.has(
														i.value,
													)}
													onChange={(e) =>
														handleCheckBox(e)
													}
												/>
											);
										})}
									</div>
								</>
							)}

							<span className={'filter__title'}>
								{categoryMassage?.title}
							</span>
							<div className="filter__checkboxes-grid">
								{filterData.services.massage.map((i) => {
									return (
										<Checkbox
											border={false}
											key={i.id}
											name={i.name}
											id={i.id}
											value={i.value}
											title={t(i.title)}
											count={i.count}
											checked={localFilter.services.has(
												i.value,
											)}
											onChange={(e) => handleCheckBox(e)}
										/>
									);
								})}
							</div>

							{!localFilter.service_type.has('massage') && (
								<>
									<span className={'filter__title'}>{`${
										categoryBdsm?.title
									}  ${t('and')} ${
										categoryExtreme?.title
									}`}</span>
									<div className="filter__checkboxes-grid">
										{filterData.services.bdsm.map((i) => {
											return (
												<Checkbox
													border={false}
													key={i.id}
													name={i.name}
													id={i.id}
													value={i.value}
													title={t(i.title)}
													count={i.count}
													checked={localFilter.services.has(
														i.value,
													)}
													onChange={(e) =>
														handleCheckBox(e)
													}
												/>
											);
										})}
										{filterData.services.extrim.map((i) => {
											return (
												<Checkbox
													border={false}
													key={i.id}
													name={i.name}
													id={i.id}
													value={i.value}
													title={t(i.title)}
													checked={localFilter.services.has(
														i.value,
													)}
													onChange={(e) =>
														handleCheckBox(e)
													}
												/>
											);
										})}
									</div>
								</>
							)}

							{!localFilter.service_type.has('massage') && (
								<>
									<span className={'filter__title'}>
										{categoryAdditional?.title}
									</span>
									<div className="filter__checkboxes-grid">
										{filterData.services.extra.map((i) => {
											return (
												<Checkbox
													border={false}
													key={i.id}
													name={i.name}
													id={i.id}
													value={i.value}
													title={t(i.title)}
													checked={localFilter.services.has(
														i.value,
													)}
													onChange={(e) =>
														handleCheckBox(e)
													}
												/>
											);
										})}
									</div>
								</>
							)}
						</>
					</div>
					<div className="filter__box p-h-8 filter--min-tariffs">
						<span className={'filter__title'}>{t('mintime')}</span>
						<div className="filter__checkbox-buttons">
							{filterData.min_time.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										checked={localFilter.min_time.has(
											i.value,
										)}
										buttonCheckbox={true}
										count={i.count}
										onChange={(e) => handleRadio(e)}
									/>
								);
							})}
						</div>
					</div>
				</Accordion>

				<Accordion
					name={'language'}
					title={t('language')}
					edited={checkIfEdited(['languages'])}
					resetFunc={() => handleResetAcc(['languages'])}
				>
					<div className="filter__box p-h-8 filter__checkbox-buttons filter--language">
						{langData.length > 0 &&
							langData.map((i) => {
								return (
									<Checkbox
										border={false}
										key={`languages-${i.id}`}
										name={'languages'}
										id={`languages-${i.id}`}
										value={i.short_name}
										title={i.name}
										flag={i.short_name.toUpperCase()}
										type={'flag'}
										checked={localFilter.languages.has(
											i.short_name,
										)}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
					</div>
				</Accordion>

				<Accordion
					name={'nationality'}
					title={t('nationality')}
					edited={checkIfEdited(['nationality', 'ethnos'])}
					resetFunc={() => {
						handleResetAcc(['ethnos', 'nationality']);
					}}
				>
					<div className="filter__box p-h-8 filter--ethnos">
						<span className={'filter__title'}>
							{t('ethnicity')}
						</span>

						<div className="filter__checkboxes-grid">
							{filterData.ethnos.map((i) => {
								return (
									<Checkbox
										border={false}
										key={i.id}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										checked={localFilter.ethnos.has(
											i.value,
										)}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
						</div>
					</div>
					<div className="filter__box p-h-8 filter__checkbox-buttons filter--national">
						{nationData.length > 0 &&
							nationData.map((i) => {
								return (
									<Checkbox
										border={false}
										key={`nation-${i.id}`}
										name={'nationality'}
										id={`nation-${i.id}`}
										value={i.code}
										title={i.name}
										type={'flag'}
										flag={i.code.toUpperCase()}
										checked={localFilter.nationality.has(
											i.code,
										)}
										onChange={(e) => {
											handleCheckBox(e);
										}}
									/>
								);
							})}
					</div>
				</Accordion>

				<Accordion
					name={'search'}
					title={t('search')}
					edited={localFilter.radius.value.size > 0}
					resetFunc={() => {
						setCurrentAddress([
							{
								address: '',
								city: currentCity?.name || '',
								city_id: currentCity?.id,
							},
						]);
						setLocalFilter((prev) => {
							return {
								...prev,
								radius: {
									latitude: 0,
									longitude: 0,
									value: new Set(),
								},
							};
						});
					}}
				>
					<div className="filter__box p-h-8 filter--radius">
						<div className="search filter__map-input">
							<fieldset>
								<legend>{t('youraddress')}</legend>

								<GeoInput
									value={currentAddress[0].address}
									name={'location'}
									id="geosuggest__input-create"
									clazz={`header__search-input`}
									placeholder={t('adressplaceholder')}
									city={currentCity.title}
									setValue={handleChange}
								/>
							</fieldset>
							{/* <span className={"filter__map-title"}>{t("youraddress")}</span> */}
							<Button
								title={t('chekmyplace')}
								onClick={onLocationClick}
								name={'location'}
								square={true}
								buttonType={'submit'}
								size={'l'}
								clazz={'button button--square '}
								// clazz={
								// 	FILTER.isActive
								// 		? 'button button--square active'
								// 		: 'button button--square'
								// }
								type="submit"
							>
								<Icon
									size={'xl'}
									spritePath={'location'}
								/>
							</Button>
						</div>

						<div className="filter__checkbox-buttons">
							{filterData.radius.map((i) => {
								return (
									<Checkbox
										key={i.id}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										buttonCheckbox={true}
										disabled={
											localFilter.radius.latitude === 0 &&
											localFilter.radius.longitude === 0
										}
										checked={localFilter.radius.value.has(
											i.value,
										)}
										onChange={() => handleRadiusCheckBox(i)}
									/>
								);
							})}
						</div>
					</div>
				</Accordion>

				<Accordion
					name={'lastvisit'}
					title={t('lastvisit')}
					edited={checkIfEdited(['last_visit'])}
					resetFunc={() => {
						handleResetAcc(['last_visit']);
					}}
				>
					<div className="filter__box p-h-8">
						<div className="filter__checkbox-buttons filter--last-visit">
							{filterData.last_visit.map((i) => {
								return (
									<Checkbox
										key={i.id}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										buttonCheckbox={true}
										flag={i.value}
										checked={localFilter.last_visit.has(
											i.value,
										)}
										onChange={(e) => {
											handleRadio(e);
										}}
									/>
								);
							})}
						</div>
					</div>
				</Accordion>

				<div
					className={`filter__bottom ${
						isFilterEdit ? 'fd-column' : ''
					}`}
				>
					{!isFilterEdit && (
						<>
							<Button
								onClick={async () => {
									await handleApplyFilter();
								}}
								buttonType={'submit'}
								disabled={count === 0}
								size={'m'}
								clazz={'button--green filter__submit'}
							>
								{`${t('show')} ${isLoadingAmmount ? '...' : count} ${plural_end}`}
							</Button>

							<Button
								onClick={async () => {
									resetAllFilters();

									// handleClose();
								}}
								clazz="header__reset hover p-h-8 ml-0"
							>
								{t('reset')}
							</Button>
						</>
					)}

					{isFilterEdit && (
						<fieldset>
							<legend>
								{(editFilterError && (
									<span className={'input-label__error'}>
										{t('required')}
									</span>
								)) || <span>{t('filtername')}</span>}
							</legend>
							<InputInLabel
								clazz={`${
									editFilterError ? ' _error' : 'w-100'
								}`}
								id={'filter-edit-name'}
								placeholder={t('')}
								value={editFilterName}
								onChange={(e) => {
									setEditFilterName(e.target.value);
									if (editFilterError) {
										setEditFilterError(false);
									}
								}}
								autocomplete={'off'}
							></InputInLabel>
						</fieldset>
					)}
					{!isLoading ? (
						<EditFilterWrapper>
							{isFilterEdit && (
								<Button
									onClick={() => {
										handleSaveFilter();
									}}
									size={'m'}
									clazz={'filter__submit button--green'}
								>
									{t('savefilters')}
								</Button>
							)}

							<Button
								clazz={'button--tetriary'}
								onClick={handleClose}
							>
								{t('toclose')}
								<Icon spritePath={'close'} />
							</Button>
						</EditFilterWrapper>
					) : (
						<Loader
							height={'64'}
							decimal={'px'}
							color={'white'}
						></Loader>
					)}

					{/* <LoadingButton /> */}
				</div>
			</>
			{/* AI FILTER MODAL */}
			<Popup
				open={isAIActive}
				setOpen={() => {
					setIsAIActive(false);
				}}
				id={'ai-filter'}
			>
				<div className="d-flex align-center justify-center gap-16 mt-64">
					<InputInLabel
						type="textarea"
						clazz={'ai-textfield'}
						id={'filter-ai-text'}
						placeholder={t('aitextplaceholder')}
						value={aiTextfield}
						onChange={(e) => {
							setAiTextfield(e.target.value);
						}}
						autocomplete={'off'}
					/>
					<button
						onClick={handleRecordAi}
						className="button button-icon button--secondary"
					>
						<i className="icon icon-l icon--volume" />
					</button>
					<button
						onClick={handleApplyAi}
						className="button button--primary button-square"
					>
						{t('apply')}
					</button>
				</div>
			</Popup>
		</Popup>
	);
};

export default Filter;
